import { useAtom } from "jotai";
import { useCallback, useRef } from "react";
import Axios from "axios";
import moment from "moment";
import { Setting } from "library/types/types";
import { handleAxiosError } from "library/utils";
import api from "../service/api";
import { useMainContext } from "../providers/MainProvider";
import { useHasAnyRole } from "hooks/useUserRoles";
import { RolePermissions } from "library/constants/roles";
import {
  proposalsPageSettingsState,
  proposalsPageSettingsLoadStatusState,
  proposalsPageRowsState,
  proposalsPageOfferItemsLoadStatusState,
  proposalsPageShowInvalidatedState,
  proposalsPageSortState,
  proposalsPageSearchState,
  proposalsPageTotalState,
  proposalsPageInvalidateOfferStatusState,
  proposalsPageSubmitInsurerReportFormStatusState,
  proposalsPageXlsVehicleItemsState,
  proposalsPageSelectedViewIdState,
  proposalsPageProductFilterState,
  proposalsPageShowModeState,
} from "./ProposalsPageStore";

// The show mode is sent only by admins and read-only admins; the backend lists only valid entries (not marked as error) without it.
const useShowModeParam = () => {
  const [showMode] = useAtom(proposalsPageShowModeState);
  const canChooseShowMode = useHasAnyRole(
    RolePermissions.canChooseOffersShowMode()
  );
  return canChooseShowMode ? showMode : undefined;
};

// Hook for setting store values
export const useSetProposalsPageStoreValue = () => {
  const [, setSettings] = useAtom(proposalsPageSettingsState);
  const [, setSettingsLoadStatus] = useAtom(
    proposalsPageSettingsLoadStatusState
  );
  const [, setVehicleItems] = useAtom(proposalsPageRowsState);
  const [, setOfferItemsLoadStatus] = useAtom(
    proposalsPageOfferItemsLoadStatusState
  );
  const [, setShowInvalidated] = useAtom(proposalsPageShowInvalidatedState);
  const [, setSort] = useAtom(proposalsPageSortState);
  const [, setSearchTerm] = useAtom(proposalsPageSearchState);
  const [, setCount] = useAtom(proposalsPageTotalState);
  const [, setInvalidateOfferStatus] = useAtom(
    proposalsPageInvalidateOfferStatusState
  );
  const [, setSubmitInsurerReportFormStatus] = useAtom(
    proposalsPageSubmitInsurerReportFormStatusState
  );
  const [, setXlsVehicleItems] = useAtom(proposalsPageXlsVehicleItemsState);
  const [, setSelectedViewId] = useAtom(proposalsPageSelectedViewIdState);

  return useCallback(
    (name: string, value: any) => {
      switch (name) {
        case "settings":
          setSettings(value);
          break;
        case "settingsLoadStatus":
          setSettingsLoadStatus(value);
          break;
        case "vehicleItems":
          setVehicleItems(value);
          break;
        case "offerItemsLoadStatus":
          setOfferItemsLoadStatus(value);
          break;
        case "showInvalidated":
          setShowInvalidated(value);
          break;
        case "sort":
          setSort(value);
          break;
        case "searchTerm":
          setSearchTerm(value);
          break;
        case "count":
          setCount(value);
          break;
        case "invalidateOfferStatus":
          setInvalidateOfferStatus(value);
          break;
        case "submitInsurerReportFormStatus":
          setSubmitInsurerReportFormStatus(value);
          break;
        case "xlsVehicleItems":
          setXlsVehicleItems(value);
          break;
        case "selectedViewId":
          setSelectedViewId(value);
          break;
        default:
          console.warn(`Unknown store property: ${name}`);
      }
    },
    [
      setSettings,
      setSettingsLoadStatus,
      setVehicleItems,
      setOfferItemsLoadStatus,
      setShowInvalidated,
      setSort,
      setSearchTerm,
      setCount,
      setInvalidateOfferStatus,
      setSubmitInsurerReportFormStatus,
      setXlsVehicleItems,
      setSelectedViewId,
    ]
  );
};

// Every list load gets an id; only the latest one may update the rows, so a slow
// earlier response cannot overwrite a newer reload (e.g. the one started by "Apply").
let latestOfferLoadId = 0;

// Hook for loading offers
// Uses a ref for columns so the callback always reads the latest value at call time.
// `payload.columns` overrides them (e.g. columns applied in the same event), and
// `payload.force` starts the load even while another one is still running.
export const useLoadOffers = () => {
  const [offerItemsLoadStatus, setOfferItemsLoadStatus] = useAtom(
    proposalsPageOfferItemsLoadStatusState
  );
  const [vehicleItems, setVehicleItems] = useAtom(proposalsPageRowsState);
  const [, setCount] = useAtom(proposalsPageTotalState);
  const [sort] = useAtom(proposalsPageSortState);
  const [searchTerm] = useAtom(proposalsPageSearchState);
  const [productFilter] = useAtom(proposalsPageProductFilterState);
  const showMode = useShowModeParam();
  const { columns, columnsReady } = useMainContext();
  const columnsRef = useRef(columns);
  columnsRef.current = columns;
  const columnsReadyRef = useRef(columnsReady);
  columnsReadyRef.current = columnsReady;

  return useCallback(
    async (payload: any, onSuccess?: (userType: string) => void) => {
      // Don't fetch until columns are initialized from default view
      if (!columnsReadyRef.current) return;
      const {
        reset = false,
        skip = 0,
        top = 40,
        columns: payloadColumns,
        force = false,
      } = payload;
      const cancelSource = Axios.CancelToken.source();
      if (offerItemsLoadStatus === "loading" && !force) return;
      const loadId = ++latestOfferLoadId;

      try {
        setOfferItemsLoadStatus("loading");

        const response = await api.searchOffers({
          columns: payloadColumns ?? columnsRef.current,
          skip,
          top,
          search: searchTerm,
          sort,
          cancelToken: cancelSource.token,
          productFilter,
          showMode,
        });
        if (loadId !== latestOfferLoadId) return;

        let newOffers = response.data.value;
        if (reset === false) {
          newOffers = [...vehicleItems, ...response.data.value];
        }

        setVehicleItems(hydrateOffers(newOffers));
        setCount(response.data["@odata.count"]);
        setOfferItemsLoadStatus("success");
        onSuccess && onSuccess("");
      } catch (error: any) {
        if (loadId !== latestOfferLoadId) return;
        if (error.code !== "ERR_CANCELED") {
          handleAxiosError(error);
          setOfferItemsLoadStatus("error");
        }
      }
    },
    [
      offerItemsLoadStatus,
      setOfferItemsLoadStatus,
      vehicleItems,
      setVehicleItems,
      setCount,
      sort,
      searchTerm,
      productFilter,
      showMode,
    ]
  );
};

// Hook for loading offers for report (Excel / PDF export).
// Azure AI Search caps a single page at 1000 documents, so the full result set
// is fetched in pages until `@odata.count` rows have been collected.
const REPORT_PAGE_SIZE = 1000;

export const useLoadOffersForReport = () => {
  const [offerItemsLoadStatus, setOfferItemsLoadStatus] = useAtom(
    proposalsPageOfferItemsLoadStatusState
  );
  const { columns } = useMainContext();
  const columnsRef2 = useRef(columns);
  columnsRef2.current = columns;
  const [sort] = useAtom(proposalsPageSortState);
  const [searchTerm] = useAtom(proposalsPageSearchState);
  const [productFilter] = useAtom(proposalsPageProductFilterState);
  const showMode = useShowModeParam();

  return useCallback(
    async (payload: {
      onSuccess?: (rows: any[]) => void;
      onError?: (error: any) => void;
      /** additional OData filter combined with the list filters (e.g. the export period) */
      extraFilter?: string;
    }) => {
      const { onSuccess, onError, extraFilter } = payload;
      const cancelSource = Axios.CancelToken.source();

      try {
        if (offerItemsLoadStatus === "loading") return;
        setOfferItemsLoadStatus("loading");

        const allOffers: any[] = [];
        let skip = 0;
        let total = 0;

        do {
          const response = await api.searchOffers({
            columns: columnsRef2.current,
            skip,
            top: REPORT_PAGE_SIZE,
            search: searchTerm,
            sort,
            cancelToken: cancelSource.token,
            productFilter,
            extraFilter,
            showMode,
          });

          const page: any[] = response.data.value ?? [];
          total = response.data["@odata.count"] ?? page.length;
          allOffers.push(...page);
          skip += page.length;

          if (page.length === 0) break;
        } while (skip < total);

        setOfferItemsLoadStatus("success");
        onSuccess && onSuccess(hydrateOffers(allOffers));
      } catch (error: any) {
        onError && onError(error);
        if (error.code !== "ERR_CANCELED") {
          handleAxiosError(error);
          setOfferItemsLoadStatus("error");
        } else {
          setOfferItemsLoadStatus("success");
        }
      }
    },
    [
      offerItemsLoadStatus,
      setOfferItemsLoadStatus,
      sort,
      searchTerm,
      productFilter,
      showMode,
    ]
  );
};

// Hook for loading settings
export const useLoadSettings = () => {
  const [settingsLoadStatus, setSettingsLoadStatus] = useAtom(
    proposalsPageSettingsLoadStatusState
  );
  const [, setSettings] = useAtom(proposalsPageSettingsState);
  const [, setSelectedViewId] = useAtom(proposalsPageSelectedViewIdState);

  return useCallback(
    async (applicationCodeId: number) => {
      if (settingsLoadStatus === "loading") return;
      try {
        setSettingsLoadStatus("loading");
        const res = await api.getAppSettings(applicationCodeId);
        setSettings(res.data);
        setSettingsLoadStatus("success");

        const defaultView = res.data.find((s: Setting) => s.isDefault);
        if (defaultView) {
          setSelectedViewId(defaultView.userAppSettingId);
        }
      } catch (err) {
        handleAxiosError(err);
        setSettingsLoadStatus("error");
      }
    },
    [settingsLoadStatus, setSettingsLoadStatus, setSettings, setSelectedViewId]
  );
};

// Hook for invalidating offer
export const useInvalidateOffer = () => {
  const [invalidateOfferStatus, setInvalidateOfferStatus] = useAtom(
    proposalsPageInvalidateOfferStatusState
  );

  return useCallback(
    async (payload: any, onSuccess?: () => void) => {
      if (invalidateOfferStatus === "loading") return;
      try {
        setInvalidateOfferStatus("loading");
        await api.invalidateOffer(payload);
        setInvalidateOfferStatus("success");
        onSuccess && onSuccess();
      } catch (err) {
        handleAxiosError(err);
        setInvalidateOfferStatus("error");
      }
    },
    [invalidateOfferStatus, setInvalidateOfferStatus]
  );
};

// Hook for submitting insurer report form
export const useSubmitInsurerReportForm = () => {
  const [, setSubmitInsurerReportFormStatus] = useAtom(
    proposalsPageSubmitInsurerReportFormStatusState
  );

  return useCallback(
    async (data: any, t: any, onSuccess?: (message: string) => void) => {
      setSubmitInsurerReportFormStatus("loading");
      const payload: any = {
        DateStart: data.DateStart
          ? moment(data.DateStart).format("YYYY-MM-DD")
          : null,
        DateEnd: data.DateEnd
          ? moment(data.DateEnd).format("YYYY-MM-DD")
          : null,
      };

      try {
        // Note: sendInsurerReportPolicies doesn't exist in QTG API, so this might need adjustment
        // const response = await api.sendInsurerReportPolicies(payload);
        // For now, using a generic post method
        const response = await api.post("/offers/insurer", payload);
        setSubmitInsurerReportFormStatus("success");
        onSuccess && onSuccess(response.data.Message);
        return response;
      } catch (error) {
        handleAxiosError(error);
        setSubmitInsurerReportFormStatus("error");
        throw error;
      }
    },
    [setSubmitInsurerReportFormStatus]
  );
};

// Hook for creating setting
export const useCreateSetting = () => {
  const [settingsLoadStatus, setSettingsLoadStatus] = useAtom(
    proposalsPageSettingsLoadStatusState
  );
  const [, setSettings] = useAtom(proposalsPageSettingsState);
  const [, setSelectedViewId] = useAtom(proposalsPageSelectedViewIdState);

  return useCallback(
    async (newSetting: Setting) => {
      if (settingsLoadStatus === "loading") return;
      try {
        setSettingsLoadStatus("loading");
        const res = await api.addAppSetting(newSetting);
        const allSettings = res.data;
        setSettings(allSettings);
        setSettingsLoadStatus("success");

        const newSettingId = allSettings.find(
          (s: Setting) => s.userAppSettingName === newSetting.userAppSettingName
        )?.userAppSettingId;

        if (newSettingId) {
          setSelectedViewId(newSettingId);
        }
      } catch (err) {
        handleAxiosError(err);
        setSettingsLoadStatus("error");
      }
    },
    [settingsLoadStatus, setSettingsLoadStatus, setSettings, setSelectedViewId]
  );
};

// Hook for deleting setting
export const useDeleteSetting = () => {
  const [settingsLoadStatus, setSettingsLoadStatus] = useAtom(
    proposalsPageSettingsLoadStatusState
  );
  const [, setSettings] = useAtom(proposalsPageSettingsState);

  return useCallback(
    async (id: number) => {
      if (settingsLoadStatus === "loading") return;
      try {
        setSettingsLoadStatus("loading");
        const res = await api.deleteAppSetting(id);
        const allSettings = res.data;
        setSettings(allSettings);
        setSettingsLoadStatus("success");
      } catch (err) {
        handleAxiosError(err);
        setSettingsLoadStatus("error");
      }
    },
    [settingsLoadStatus, setSettingsLoadStatus, setSettings]
  );
};

// Hook for updating setting
export const useUpdateSetting = () => {
  const [settingsLoadStatus, setSettingsLoadStatus] = useAtom(
    proposalsPageSettingsLoadStatusState
  );
  const [, setSettings] = useAtom(proposalsPageSettingsState);

  return useCallback(
    async (setting: Setting) => {
      if (settingsLoadStatus === "loading") return;
      try {
        setSettingsLoadStatus("loading");
        const res = await api.updateAppSetting(setting);
        const allSettings = res.data;
        setSettings(allSettings);
        setSettingsLoadStatus("success");
      } catch (err) {
        handleAxiosError(err);
        setSettingsLoadStatus("error");
      }
    },
    [settingsLoadStatus, setSettingsLoadStatus, setSettings]
  );
};

// Utility function to hydrate offers (same as in original store actions)
export const hydrateOffers = (newOffers: any) => {
  const columns = newOffers.map((row: any) => {
    // Determine product type based on OfferedInsurersCasco and OfferedInsurersMtpl
    let product: string | null = null;

    const hasCasco =
      row.OfferedInsurersCasco && row.OfferedInsurersCasco.length > 0;
    const hasMtpl =
      row.OfferedInsurersMtpl && row.OfferedInsurersMtpl.length > 0;

    if (hasCasco && hasMtpl) {
      product = "MTPL + CASCO";
    } else if (hasCasco) {
      product = "CASCO";
    } else if (hasMtpl) {
      product = "MTPL";
    }

    return {
      ...row,
      Product: product,
      inp_lessee_username:
        row.inp_lessee_type === "Persoana fizica"
          ? row.inp_lessee_firstname + " " + row.inp_lessee_lastname
          : row.inp_lessee_company_name,
    };
  });
  return columns;
};
